import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ChartHeaderActions } from '@/components/charts/actions';
import ContentBlock from '@/components/molecules/ContentBlock';
import EmptyState from '@/components/molecules/EmptyState';
import { useCage } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import { isValidCageId } from '@/domain/cages';
import type { HealthParameterKey } from '@/domain/health';
import { useGetHealthOverview } from '@/domain/health';
import { useChartActions } from '@/hooks/useChartActions';
import { useDateRangePicker } from '@/hooks/useDateRangePicker';

import { findHealthOverviewParameter } from '../../_utils/healthDetailOverviewHelpers';
import HealthDevelopmentLineChart from '../../HealthDashboard/components/HealthDevelopmentLineChart/HealthDevelopmentLineChart';
import {
    getHealthDevelopmentDateRangeKey,
    HEALTH_DEFAULT_DATE_RANGE_PRESET_ID,
} from '../healthDetailChartConstants';

import {
    hasHealthTrendChartData,
    mapHealthTrendToDevelopmentDatasets,
} from './healthDevelopmentChartMappers';

import chartBlockStyles from '../HealthDetailChartContentBlock.module.scss';
import styles from './HealthDevelopmentChart.module.scss';

export interface HealthDevelopmentChartProps {
    healthParameterKey: HealthParameterKey;
}

export default function HealthDevelopmentChart(props: HealthDevelopmentChartProps) {
    const { healthParameterKey } = props;
    const { t } = useTranslation('healthModule');
    const { cageId } = useCage();
    const { daypartTag, selectedFishGroupId } = useCageFilter();

    const developmentDateRangeKey = getHealthDevelopmentDateRangeKey(healthParameterKey);

    const { dateFrom, dateTo, onDateChange } = useDateRangePicker({
        key: developmentDateRangeKey,
        defaultPresetId: HEALTH_DEFAULT_DATE_RANGE_PRESET_ID,
    });

    const { chartRef, isFullScreen, handleFullScreenToggle, fullScreenContainerRef } =
        useChartActions();

    const overviewQueryParams = useMemo(
        () => ({
            daypartTag,
            from: dateFrom,
            to: dateTo,
            fishGroupId: selectedFishGroupId,
        }),
        [daypartTag, dateFrom, dateTo, selectedFishGroupId]
    );

    const {
        data: overview,
        isLoading,
        isFetching,
    } = useGetHealthOverview(cageId, overviewQueryParams, {
        enabled: isValidCageId(cageId) && Boolean(dateFrom && dateTo),
        staleTime: 5 * 60 * 1000,
    });

    const loading = isLoading || isFetching;

    const parameter = useMemo(
        () => findHealthOverviewParameter(overview, healthParameterKey),
        [overview, healthParameterKey]
    );

    const trend = parameter?.trend ?? [];
    const hasNoData = !loading && !hasHealthTrendChartData(trend);

    const datasets = useMemo(() => mapHealthTrendToDevelopmentDatasets(trend), [trend]);

    return (
        <ContentBlock
            title={t('detail.common.developmentTitle')}
            size="compact"
            spacing="none"
            fill
            className={`${chartBlockStyles.chartBlock} ${chartBlockStyles.fixedHeightChartBlock}`}
            containerRef={fullScreenContainerRef}
            headerActions={
                <ChartHeaderActions
                    chartRef={chartRef}
                    chartName="health-development-chart"
                    isFullScreen={isFullScreen}
                    disabled={loading}
                    onFullScreenToggle={handleFullScreenToggle}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onDateChange={onDateChange}
                    defaultPresetId={HEALTH_DEFAULT_DATE_RANGE_PRESET_ID}
                />
            }>
            <div className={`${chartBlockStyles.chartBlockBody} ${styles.chartBody}`}>
                {hasNoData ? (
                    <EmptyState
                        title={t('detail.common.noData')}
                        appearance="quiet"
                        live="polite"
                        className={styles.noData}
                    />
                ) : (
                    <HealthDevelopmentLineChart
                        datasets={datasets}
                        parameterTrend={trend}
                        isLoading={loading}
                        showHeader={false}
                        chartRef={chartRef}
                    />
                )}
            </div>
        </ContentBlock>
    );
}
